import React, { useEffect, useRef, useState } from 'react';
import StickyNotesBoard from './StickyNotesBoard';
import { toShippingCommentNotes } from './shippingCommentNotes';
import './StickyNotes.css';

/* Only one sticky-notes board is open at a time across the grid. A board with an
   unsaved draft refuses to close, so opening another cell nudges it instead. */
let activeBoard = null; // { id, tryClose }

function claimBoard(id, tryClose) {
    if (activeBoard && activeBoard.id !== id && !activeBoard.tryClose()) return false;
    activeBoard = { id, tryClose };
    return true;
}

function releaseBoard(id) {
    if (activeBoard?.id === id) activeBoard = null;
}

let nextCellId = 0;

/** Cetec text often arrives with literal <br> tags. Show them as line breaks. */
const displayText = (value) => String(value || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li)>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&quot;/gi, '"')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{2,}/g, '\n')
    .trim();

function NoteFace({ text, color, locked, onOpen, title }) {
    return (
        <button
            type="button"
            className={`sticky-inline-card sticky-color-${color}${locked ? ' is-pinned' : ''}`}
            onClick={onOpen}
            title={title || text}
        >
            <span className="sticky-note-paper">
                <span className="sticky-note-text">{text}</span>
            </span>
        </button>
    );
}

/**
 * Sticky Notes cell. The badge counts permanent instructions, the shipping
 * comment, and extra comments together. Shipping uses its own color. Extra
 * comments and Add sit at the bottom of a short scroll.
 */
export default function StickyNotesCell({
    orderNumber,
    orderLineId,
    customerName,
    label,
    notes = [],
    pinned = [],
    shippingComments = [],
    customerId = null,
    onAddShipping,
    onUpdateShipping,
    onRemoveShipping,
    onAdd,
    onUpdate,
    onRemove,
}) {
    const anchorRef = useRef(null);
    const boardApiRef = useRef(null);
    const idRef = useRef(null);
    if (idRef.current == null) {
        nextCellId += 1;
        idRef.current = `sticky-cell-${nextCellId}`;
    }
    const [board, setBoard] = useState(null);
    const [dropId, setDropId] = useState(null);

    useEffect(() => () => releaseBoard(idRef.current), []);

    useEffect(() => {
        if (!dropId) return undefined;
        const timer = setTimeout(() => setDropId(null), 700);
        return () => clearTimeout(timer);
    }, [dropId]);

    if (!orderNumber) return <span className="text-muted">—</span>;

    const open = (adding, focusNoteId = null) => {
        if (board) return;
        const ok = claimBoard(idRef.current, () => (boardApiRef.current ? boardApiRef.current.tryClose() : true));
        if (!ok) return;
        setBoard({ adding, focusNoteId, openedAt: Date.now() });
    };

    const close = () => {
        releaseBoard(idRef.current);
        setBoard(null);
        const trigger = anchorRef.current?.querySelector('.sticky-inline-card, .sticky-group-add');
        trigger?.focus();
    };

    const handleAdd = async (fields) => {
        const created = await onAdd(orderNumber, { ...fields, orderLineId });
        if (created?.id) setDropId(created.id);
        return created;
    };

    const permanentNotes = pinned.filter((note) => note.locked || note.source === 'cetec');
    const manualShipping = pinned.filter((note) => note.source === 'manual' || (!note.locked && note.source !== 'cetec'));
    const shippingNotes = toShippingCommentNotes(shippingComments);
    const extraNotes = [...manualShipping, ...notes];
    const permanentText = permanentNotes.map((note) => displayText(note.text)).filter(Boolean).join('\n');
    const shippingText = shippingNotes.map((note) => displayText(note.text)).filter(Boolean).join('\n');
    const total = permanentNotes.length + shippingNotes.length + extraNotes.length;

    return (
        <div className="sticky-notes-groups" ref={anchorRef}>
            {total > 0 && <span className="sticky-total-count">{total}</span>}
            <div className={`sticky-priority-scroll${dropId ? ' is-drop' : ''}`}>
                {permanentText && (
                    <NoteFace
                        text={permanentText}
                        color="orange"
                        locked
                        onOpen={() => open(false, permanentNotes[0]?.id)}
                    />
                )}
                {shippingText && (
                    <NoteFace
                        text={shippingText}
                        color="periwinkle"
                        locked
                        onOpen={() => open(false, shippingNotes[0]?.id)}
                    />
                )}
                {extraNotes.map((note) => (
                    <NoteFace
                        key={note.id}
                        text={displayText(note.text)}
                        color={note.color || 'green'}
                        locked={false}
                        onOpen={() => open(false, note.id)}
                    />
                ))}
                <button type="button" className="sticky-group-add" onClick={() => open(true)}>
                    + Add
                </button>
            </div>
            {board && (
                <StickyNotesBoard
                    key={board.openedAt}
                    anchorRef={anchorRef}
                    apiRef={boardApiRef}
                    orderNumber={orderNumber}
                    label={label || orderNumber}
                    customerName={customerName}
                    notes={notes}
                    pinned={pinned}
                    shippingComments={shippingNotes}
                    canAddShipping={Boolean(customerId && onAddShipping)}
                    onAddShipping={(fields) => onAddShipping(customerId, fields)}
                    onUpdateShipping={(note, changes) => onUpdateShipping(note, changes)}
                    onRemoveShipping={(note) => onRemoveShipping(note)}
                    startAdding={board.adding}
                    focusNoteId={board.focusNoteId}
                    onAdd={handleAdd}
                    onUpdate={(note, changes) => onUpdate(orderNumber, note, changes)}
                    onRemove={(note) => onRemove(orderNumber, note)}
                    onClose={close}
                />
            )}
        </div>
    );
}
